import { Checkbox } from '@mui/material';
import { useGridEvent } from 'components/shared/data/DataGrid/hooks';
import { ICellRendererParams } from 'ag-grid-community';
import React, { FC, useState } from 'react';
import { muiStyles } from 'utils/design';

const useStyles = muiStyles(() => ({
  root: {
    padding: '1px',
    color: '#333',
  },
}));

/**
 * Checkbox cell renderer driven by AG Grid's own selection model (node.isSelected()).
 * Drop-in replacement for AG Grid's built-in checkboxes.
 * Use together with rowSelectionCheckboxes={false} on DataGrid.
 * Respects node.selectable (set by isRowSelectable grid option).
 */
type AgGridCheckboxCellParams = ICellRendererParams & {
  isDisabled?: (row: unknown, node: unknown) => boolean;
  showDisabled?: boolean;
};

export const AgGridCheckboxCell: FC<AgGridCheckboxCellParams> = ({ node, api, isDisabled, showDisabled }) => {
  const classes = useStyles();
  const [checked, setChecked] = useState(node.isSelected() ?? false);
  const isSelectable = node.selectable ?? false;
  const disabledByRowRule = isDisabled ? isDisabled(node.data, node) : false;
  const shouldRenderDisabled = !!showDisabled && (!isSelectable || disabledByRowRule);

  useGridEvent(api, 'selectionChanged', () => {
    setChecked(node.isSelected() ?? false);
  });

  if (!isSelectable && !shouldRenderDisabled) {
    return null;
  }

  return (
    <Checkbox
      color="primary"
      classes={classes}
      checked={checked}
      disabled={!isSelectable || disabledByRowRule}
      onChange={(_, value) => {
        if (!isSelectable || disabledByRowRule) {
          return;
        }
        node.setSelected(value);
      }}
      disableRipple
    />
  );
};
